import { ActivityFieldGrouping, FilterOption, FilterOptionType } from "../../common/components/filterPopover/FilterPopover.types";
import { useTranslation } from "../../localization/useTranslation";
import { EntityType } from "../../common/Common.types";
import { getSelectableBusinessAreasUrl, selectableGroupEntitiesForUserUrl, selectableTagsUrl } from "../../common/hooks/useSelectableItemQueries";
import { getResponsibleQueryUrl } from "../../user/hooks/useResponsibleQuery";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { AnnualWheelActivityGeneralType } from "../annualWheelDialog/AnnualWheelDialog.types";
import { ApprovalOption, TaskStateEnum } from "../AnnualWheel.types";
import { AnnualWheelPriority } from "../annualWheelDialog/AnnualWheelDialog.types";
import { ActivitiesFilterRequest, defaultActivitiesPageSize } from "./ActivitiesSearch.types";
import { hasFilterValue } from "@common/components/filterBar/filterPinning.ts";
import { SortDirection } from "../../contract/ContractFilter.types";
import { setFirstLetterToLowerCase } from "../../common/stringOperations";
import { getEnumValues } from "../../common/enumOperations";
import { hydrateFilterOperators, uiOperatorToWire } from "@common/components/filterBar/filterOperatorHydration";

export function useActivitiesFilter(filterData?: ActivitiesFilterRequest) {
    const { translateString, translateAnnualWheelActivityGeneralTypeEnum } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures, contractManagementFeatures } = usePlanContext();

    const activityFilterOptions = getFilterOptions();

    hydrateFilterOperators(activityFilterOptions, filterData, FILTER_ID_TO_BACKEND_PROPERTY);

    return { activityFilterOptions, isLoading: false };

    function getFilterOptions(): Array<FilterOption> {
        let filterOptions: Array<FilterOption> = [
            {
                id: "custom-id",
                label: translateString("id"),
                type: FilterOptionType.TextField,
                value: filterData?.customId?.value ?? "",
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },
            {
                id: "activity-ids",
                label: translateString("activity"),
                type: FilterOptionType.SelectField,
                value: filterData?.activities?.value ?? "",
                entityType: EntityType.AnnualWheelActivity,
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },
            {
                id: "description",
                label: translateString("description"),
                type: FilterOptionType.TextField,
                value: filterData?.description?.value ?? "",
                grouping: ActivityFieldGrouping.DescriptionAndScope,
            },
            {
                id: "generalTypes",
                label: translateString("type"),
                type: FilterOptionType.SelectField,
                value: filterData?.generalTypes?.value ?? "",
                fixedData: getEnumValues(AnnualWheelActivityGeneralType).map((enumValue) => ({
                    id: enumValue.toString(),
                    name: translateAnnualWheelActivityGeneralTypeEnum(enumValue.toString()),
                })),
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },
            {
                id: "status",
                label: translateString("status"),
                type: FilterOptionType.SelectField,
                value: filterData?.status?.value ?? "",
                fixedData: getEnumValues(TaskStateEnum).map((enumValue) => ({
                    id: enumValue.toString(),
                    name: translateString(setFirstLetterToLowerCase(TaskStateEnum[enumValue].toString())),
                })),
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },
            {
                id: "priorities",
                label: translateString("priority"),
                type: FilterOptionType.SelectField,
                value: filterData?.priorities?.value ?? "",
                fixedData: getEnumValues(AnnualWheelPriority).map((enumValue) => ({
                    id: enumValue.toString(),
                    name: translateString(setFirstLetterToLowerCase(AnnualWheelPriority[enumValue].toString())),
                })),
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            },
            {
                id: "responsible",
                label: translateString("responsible"),
                type: FilterOptionType.SelectField,
                value: filterData?.responsibles?.value ?? "",
                dataUrl: getResponsibleQueryUrl(),
                grouping: ActivityFieldGrouping.PeopleAndOwnership,
            },
            {
                id: "assignee-ids",
                label: translateString("assignees"),
                type: FilterOptionType.SelectField,
                value: filterData?.assignees?.value ?? "",
                dataUrl: getResponsibleQueryUrl(),
                grouping: ActivityFieldGrouping.PeopleAndOwnership,
            },
            {
                id: "business-area-ids",
                label: translateString("businessArea"),
                type: FilterOptionType.SelectField,
                value: filterData?.businessAreas?.value ?? "",
                dataUrl: getSelectableBusinessAreasUrl(true),
                grouping: ActivityFieldGrouping.Organization,
            },
        ];

        if (platformFeatures.annualWheelComplianceArea) {
            filterOptions.push({
                id: "areas",
                label: translateString("area"),
                type: FilterOptionType.SelectField,
                value: filterData?.complianceAreas?.value ?? "",
                dataUrl: "/areas/selectableitems",
                grouping: ActivityFieldGrouping.Organization,
            });
        }

        filterOptions.push({
            id: "group-entity-ids",
            label: translateString("groupCompany"),
            type: FilterOptionType.SelectField,
            value: filterData?.groupCompanies?.value ?? "",
            dataUrl: selectableGroupEntitiesForUserUrl,
            grouping: ActivityFieldGrouping.Organization,
        });

        // The popover fetches a filter's options on hover, and the frameworks list is refused with a 403 that navigates
        // the whole application to the forbidden page. Offering this filter to a role without framework read access
        // therefore ejects the user from the app on nothing but a mouse movement.
        if (permissions.frameworkPermissions.read) {
            filterOptions.push({
                id: "framework-ids",
                label: translateString("frameworks"),
                type: FilterOptionType.SelectField,
                value: filterData?.frameworks?.value ?? "",
                dataUrl: "/Frameworks/selectableItems",
                grouping: ActivityFieldGrouping.Organization,
            });
        }

        if (permissions.contractManagementPermissions.canAccessContractsOverview) {
            filterOptions.push(
                {
                    id: "contract-ids",
                    label: translateString("contract"),
                    type: FilterOptionType.SelectField,
                    value: filterData?.contracts?.value ?? "",
                    entityType: EntityType.Contract,
                    grouping: ActivityFieldGrouping.ContractsAndRelations,
                },
                {
                    id: "external-contractparty-ids",
                    label: translateString("externalContractParties"),
                    type: FilterOptionType.SelectField,
                    value: filterData?.externalContractPartyIds?.value ?? "",
                    dataUrl: "/LegalEntity/selectable-external-entities",
                    grouping: ActivityFieldGrouping.ContractsAndRelations,
                },
                {
                    id: "internal-contractparty-ids",
                    label: translateString("internalContractParties"),
                    type: FilterOptionType.SelectField,
                    value: filterData?.internalContractPartyIds?.value ?? "",
                    dataUrl: "/LegalEntity/selectableGroupEntities",
                    grouping: ActivityFieldGrouping.ContractsAndRelations,
                }
            );
        }

        if (permissions.canAccessTags && platformFeatures.tags) {
            filterOptions.push({
                id: "tag-ids",
                label: translateString("tags"),
                type: FilterOptionType.SelectField,
                value: filterData?.tags?.value ?? "",
                dataUrl: selectableTagsUrl,
                grouping: ActivityFieldGrouping.IdentityAndBasics,
            });
        }

        filterOptions.push(
            {
                id: "start-date-range",
                label: translateString("startDate"),
                type: FilterOptionType.DateField,
                value: filterData?.startDateRange || "",
                grouping: ActivityFieldGrouping.Scheduling,
            },
            {
                id: "end-date-range",
                label: translateString("endDate"),
                type: FilterOptionType.DateField,
                value: filterData?.endDateRange || "",
                grouping: ActivityFieldGrouping.Scheduling,
            },
            {
                id: "documentationRequired",
                label: translateString("documentationRequired"),
                type: FilterOptionType.Boolean,
                value: filterData?.documentationRequired ?? "",
                grouping: ActivityFieldGrouping.DescriptionAndScope,
            },
            {
                id: "approval-options",
                label: translateString("approvedBeforeCompleted"),
                type: FilterOptionType.SelectField,
                value: filterData?.approvalOptions?.value ?? "",
                fixedData: [
                    {
                        id: ApprovalOption.ApprovalNotRequired.toString(),
                        name: translateString("approvalNotRequired"),
                    },
                    {
                        id: ApprovalOption.ApprovalNotRequiredResponsibleGetNotified.toString(),
                        name: translateString("approvalNotRequiredResponsibleGetNotified"),
                    },
                    {
                        id: ApprovalOption.ApprovalRequiredByResponsible.toString(),
                        name: translateString("approvalRequiredByResponsible"),
                    },
                ],
                grouping: ActivityFieldGrouping.PeopleAndOwnership,
            },
            {
                id: "notificationPeriodInDays",
                label: translateString("daysPriorToNotification"),
                type: FilterOptionType.Number,
                value: filterData?.notificationPeriodInDays || "",
                grouping: ActivityFieldGrouping.Scheduling,
            },
            {
                id: "recurrence",
                label: translateString("recurrence"),
                type: FilterOptionType.RecurrenceField,
                value: filterData?.recurrence || "",
                grouping: ActivityFieldGrouping.Scheduling,
            }
        );

        return filterOptions;
    }
}

export function transformActivitiesFilterToBackend(
    filterOptions: FilterOption[],
    sortByColumn?: string,
    sortDirection?: SortDirection
): ActivitiesFilterRequest {
    const result: ActivitiesFilterRequest = {};

    filterOptions.forEach((option) => {
        if (!hasFilterValue(option.value)) {
            return;
        }

        const backendProperty = FILTER_ID_TO_BACKEND_PROPERTY[option.id];
        if (!backendProperty) {
            return;
        }

        switch (backendProperty) {
            case "customId":
            case "description":
                if (typeof option.value === "string") {
                    (result as any)[backendProperty] = {
                        value: option.value,
                        filterOperator: uiOperatorToWire(option.filterOperator, option.type),
                    };
                }
                break;
            case "startDateRange":
            case "endDateRange":
                if (
                    typeof option.value === "object" &&
                    ("from" in option.value || "to" in option.value || "dateFieldEnum" in option.value || "rollingPeriod" in option.value)
                ) {
                    (result as any)[backendProperty] = option.value;
                }
                break;
            case "documentationRequired":
                result[backendProperty] = option.value as boolean;
                break;
            case "notificationPeriodInDays":
                if (typeof option.value === "object" && ("from" in option.value || "to" in option.value)) {
                    (result as any)[backendProperty] = option.value;
                }
                break;
            case "recurrence":
                if (typeof option.value === "object" && ("from" in option.value || "to" in option.value || "recurrenceFilterEnum" in option.value)) {
                    (result as any)[backendProperty] = option.value;
                }
                break;
            default:
                if (Array.isArray(option.value)) {
                    (result as any)[backendProperty] = {
                        value: option.value,
                        filterOperator: uiOperatorToWire(option.filterOperator, option.type),
                    };
                }
                break;
        }
    });

    result.pageNumber = 1;
    result.pageSize = result.pageSize ?? defaultActivitiesPageSize;
    result.sortByColumn = sortByColumn;
    result.sortDirection = sortDirection;

    return result;
}

const FILTER_ID_TO_BACKEND_PROPERTY: Record<string, keyof ActivitiesFilterRequest> = {
    "custom-id": "customId",
    description: "description",
    "activity-ids": "activities",
    generalTypes: "generalTypes",
    status: "status",
    priorities: "priorities",
    responsible: "responsibles",
    "assignee-ids": "assignees",
    "business-area-ids": "businessAreas",
    areas: "complianceAreas",
    "contract-ids": "contracts",
    "external-contractparty-ids": "externalContractPartyIds",
    "internal-contractparty-ids": "internalContractPartyIds",
    "tag-ids": "tags",
    "group-entity-ids": "groupCompanies",
    "framework-ids": "frameworks",
    "start-date-range": "startDateRange",
    "end-date-range": "endDateRange",
    documentationRequired: "documentationRequired",
    "approval-options": "approvalOptions",
    notificationPeriodInDays: "notificationPeriodInDays",
    recurrence: "recurrence",
};
